import { formatUZS, type ProductListItem } from "@elektr/shared";
import { ImageOff } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge, Card } from "@/components/ui/card";
import { QUALITY_KEYS, QUALITY_TONE } from "@/lib/catalog";
import { useT } from "@/lib/i18n";

export function ProductCard({
  product: p,
  to,
  showStats,
}: {
  product: ProductListItem;
  to: string;
  showStats?: boolean;
}) {
  const { t } = useT();
  const q = (p.quality ?? "standard") as keyof typeof QUALITY_KEYS;
  return (
    <Link to={to} className="block h-full" aria-label={p.name}>
      <Card className="flex h-full flex-col gap-2 overflow-hidden p-0 transition-shadow hover:shadow-md">
        <div className="relative aspect-[4/3] bg-muted">
          {p.cover_url ? (
            <img src={p.cover_url} alt="" loading="lazy" className="size-full object-cover" />
          ) : (
            <div className="flex size-full items-center justify-center text-muted-foreground">
              <ImageOff className="size-8" />
            </div>
          )}
          {p.discount_percent ? (
            <span className="absolute left-2 top-2 rounded-md bg-danger px-2 py-0.5 text-xs font-bold text-white">
              -{p.discount_percent}%
            </span>
          ) : null}
          {!p.is_active && (
            <span className="absolute right-2 top-2 rounded-md bg-foreground/80 px-2 py-0.5 text-xs font-bold text-background">
              {t("catalog.inactive")}
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col gap-1 p-3">
          <div className="line-clamp-2 font-semibold leading-snug">{p.name}</div>
          <div className="text-xs text-muted-foreground">
            {[p.brand, p.category_name].filter(Boolean).join(" · ")}
          </div>
          <div className="mt-auto flex flex-wrap items-baseline gap-x-2 pt-1">
            <span className="whitespace-nowrap text-lg font-bold">{formatUZS(p.price_uzs)}</span>
            {p.old_price_uzs ? (
              <span className="whitespace-nowrap text-sm text-muted-foreground line-through">
                {formatUZS(p.old_price_uzs)}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-1">
            <Badge tone={QUALITY_TONE[q]}>{t(QUALITY_KEYS[q])}</Badge>
            {!p.in_stock && <Badge tone="danger">{t("catalog.out_of_stock")}</Badge>}
          </div>
          {showStats && p.stats && (
            <div className="text-xs text-muted-foreground" data-testid="stats">
              {t("catalog.stats", {
                views: p.stats.views,
                calls: p.stats.calls,
                leads: p.stats.leads,
              })}
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}
